import { CommonModule, NgIf } from '@angular/common'
import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { RouterModule } from '@angular/router'
import { CapitalizePipe } from '../../pipes/capitalize.pipe'
import { PaginationComponent } from '../template/pagination/pagination.component'
import { select, Store } from '@ngrx/store'
import { NgbModal, NgbModalRef, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap'
import { map, Observable, of, Subject, take, takeUntil } from 'rxjs'
import { Business } from '../../store/business/business.state'
import { selectAllBusinesses } from '../../store/business/business.selectors'
import { EmployeeRequest } from '../../store/employee-request/employee-request.state'
import {
  selectEmpRequests,
  selectLoading,
  selectTotalCount
} from '../../store/employee-request/employee-request.reducer'
import { Actions, ofType } from '@ngrx/effects'
import { AuthState, selectUser } from '../../store/auth/auth.state'
import { loadBusinesses } from '../../store/business/business.actions'
import {
  loadEmployeeRequests,
  updateEmployeeRequest,
  updateEmployeeRequestFailure,
  updateEmployeeRequestsSuccess
} from '../../store/employee-request/employee-request.actions'
import { eps } from '../../services/url.service'
import { NgSelectModule } from '@ng-select/ng-select'
import { HasPermissionDirective } from '../../directives/has-permission.directive'
import { SharedModule } from '../../shared/shared.module'

export enum StatusType {
  APPROVED = 'Approved',
  CANCELLED = 'Cancelled',
  REJECTED = 'Rejected',
  PENDING = 'Pending'
}

@Component({
  selector: 'app-employee-request',
  imports: [
    ReactiveFormsModule,
    NgIf,
    CommonModule,
    RouterModule,
    CapitalizePipe,
    FormsModule,
    PaginationComponent,
    NgSelectModule,
    HasPermissionDirective,
    SharedModule
  ],
  templateUrl: './employee-request.component.html',
  styleUrl: './employee-request.component.scss'
})
export class EmployeeRequestComponent implements OnInit {
  private store = inject(Store) // Inject store using `inject()`
  @ViewChild('addBusinessModals') addBusinessModals!: TemplateRef<any>
  @ViewChild('cancelAppliedLeaveModal') cancelAppliedLeaveModal!: TemplateRef<any>

  private modalRef!: NgbModalRef
  currentPage = 1
  pageLimit = 10
  loading$: Observable<boolean> = this.store.select(state => state.designation.loading)
  error$: Observable<string | null> = this.store.select(state => state.designation.error)
  pageLastRecord = 0
  form!: FormGroup
  businesses$: Observable<Business[]> = this.store.select(selectAllBusinesses)
  empRequests$: Observable<EmployeeRequest[]> = this.store.select(selectEmpRequests)
  totalCount$!: Observable<number>
  totalCount = 0
  private destroyed$ = new Subject<void>()
  user: AuthState['user'] | null = null
  businessIdss!: string[]
  businessId!: string | null
  status = ''
  isLoading$ = this.store.pipe(select(selectLoading))
  selectedBusinessId = ''
  blockUnblockStatus = ''
  EditHoliday: any
  public selectedAttendance: any = ''
  statusEnum = StatusType
  search = ''

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private actions$: Actions
  ) {
    this.empRequests$ = this.store.select(selectEmpRequests)
    this.totalCount$ = this.store.select(selectTotalCount).pipe(
      map(totalCount => totalCount || 0) // Default to 0 if totalCount is null
    )

    // Subscribe to totalCount$ safely
    this.totalCount$.subscribe(count => {
      this.totalCount = count // Define this.totalCount in your class
    })

    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe(user => {
        this.user = user
        const businessIds = user?.businessIds // your array of business IDs

        if (businessIds && businessIds.length) {
          this.businessIdss = businessIds
          const paramString = `businessIds=${businessIds.join(',')}`
          this.store.dispatch(loadBusinesses({ params: paramString }))
        } else {
          this.loadAllBusinesses() // Load all businesses initially
        }
      })
  }

  ngOnInit() {
    this.status = 'Pending'
    this.loadRequests()

    this.totalCount$.subscribe(count => {
      this.totalCount = count
      this.updatePageLastRecord()
    })

    this.form = this.fb.group({
      status: ['', Validators.required]
    })

    this.actions$
      .pipe(
        ofType(updateEmployeeRequestsSuccess, updateEmployeeRequestFailure),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        this.form.reset()
        this.modalService.dismissAll() // or modalRef.close() if you're using one
        this.loadRequests()
      })

    this.businesses$.subscribe(businesses => {
      if (businesses.length === 1) {
        this.form.get('businessId')?.setValue(businesses[0].id)
        this.businessId = businesses[0].id
        this.onBusinessChange(businesses[0].id)
      }
    })

    this.totalCount$ = this.store.select(selectTotalCount).pipe(
      map(totalCount => totalCount || 0) // Default to 0 if totalCount is null
    )
    this.totalCount$.subscribe(count => {
      this.totalCount = count // Define this.totalCount in your class
    })
  }

  loadRequests() {
    const params: any = {}
    if (this.businessIdss?.length) {
      params.businessIds = this.businessIdss.join(',')
    }

    if (this.search) {
      params.search = this.search
    }

    if (this.status) {
      params.status = this.status
    }

    if (this.businessId) {
      params.businessId = this.businessId
    }

    if (this.status) {
      const isDisabled = this.status === 'true' ? true : this.status === 'false' ? false : null
      params.disabled = isDisabled
    }

    this.store.dispatch(
      loadEmployeeRequests({
        params: new URLSearchParams(params).toString(),
        page: this.currentPage,
        limit: this.pageLimit
      })
    )
  }

  loadAllBusinesses() {
    const params: any = {}
    if (this.businessIdss?.length) {
      params.businessIds = this.businessIdss.join(',')
    }
    params.disabled = false

    this.store.dispatch(
      loadBusinesses({
        params: new URLSearchParams(params).toString()
      })
    )
  }

  private updatePageLastRecord() {
    this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
  }

  onPageLimitChange() {
    this.currentPage = 1 // Reset to first page when limit changes
    this.loadRequests() // Load employees with new page limit
  }

  onPageChange(newPage: number): void {
    if (newPage > 0) {
      this.currentPage = newPage
      this.loadRequests() // Reload businesses for the new page
      this.updatePageLastRecord()
    }
  }

  onBusinessChange(businessId: string) {
    if (businessId) {
      const control = this.form.get('businessId')
      control?.setValue(businessId)
      control?.markAsDirty()
      control?.markAsTouched()
      control?.updateValueAndValidity()

      this.businessId = businessId
      // Call a method to fetch data based on the selected owner
      // const params = `businessId=${encodeURIComponent(businessId)}&disabled=false`

      // this.store.dispatch(
      //   loadEmployeeRequests({ params, page: this.currentPage, limit: this.pageLimit })
      // ) // Dispatch action with filter
    } else {
      // const params = 'disabled=false'
      // this.store.dispatch(
      //   loadEmployeeRequests({ params, page: this.currentPage, limit: this.pageLimit })
      // ) // Dispatch action with filter
      // Handle the case where no owner is selected
      // console.log('No business selected')
    }
    this.loadRequests()
  }

  // getDashboardRoute(): string {
  //   return this.user?.role === 'Admin' ? eps.dashboardPath : eps.employeeDashboardPath
  // }

  getDashboardRoute(): string {
    // Check if any role in userRoles array has the name 'Admin'
    const isAdmin = this.user?.userRoles?.some(ur => ur.role?.name === 'Admin')
    return isAdmin ? eps.dashboardPath : eps.employeeDashboardPath
  }

  customSearchFn = (term: string, item: any) => {
    const fullName = `${item.businessName}`.toLowerCase()
    return fullName.includes(term.toLowerCase())
  }

  openBlockUnblock(content: TemplateRef<any>, options: any = {}, status: any) {
    this.blockUnblockStatus = status
    this.EditHoliday = options
    this.form.get('fromDate')?.setValue(options.fromDate)
    this.form.get('toDate')?.setValue(options.toDate)
    this.form.get('reason')?.setValue(options.reason)
    this.form.get('leavePolicyId')?.setValue(options.leavePolicyId)
    this.form.get('userId')?.setValue(options.userId)

    const controlName = 'dateRangeforHalfDay'

    // If control doesn't exist, create it
    if (!this.form.contains(controlName)) {
      this.form.addControl(controlName, this.fb.control([]))
    }

    // Now append to it
    const currentValue = this.form.get(controlName)?.value || []
    const newValue = [...currentValue, options.dateRangeforHalfDay]
    this.form.get(controlName)?.setValue(options.dateRangeforHalfDay)
    if (status === 'Approve') {
      this.form.get('status')?.setValue('Approved')
    }
    if (status === 'Reject') {
      this.form.get('status')?.setValue('Rejected')
    }
    if (status === 'Cancel') {
      this.form.get('status')?.setValue('Cancelled')
    }

    this.modalService.open(content, { scrollable: false, centered: true, size: 'sm' })
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return
    }

    this.store.dispatch(
      updateEmployeeRequest({ id: this.EditHoliday.id, payload: this.form.value })
    )

    this.modalService.dismissAll() // Close modal
  }

  onClose() {
    // if (this.form) {
    //   this.form.reset(); // Reset the form if it is defined
    // }
    // this.modalService.dismissAll();
    // this.currentStep = 1;

    try {
      if (this.form) {
        this.form.reset()
      }
      if (this.modalRef) {
        this.modalRef.close()
      }
      this.modalService.dismissAll()
    } catch (error) {
      console.error('Error closing modal:', error)
    }
  }
  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    // console.log('control name', control)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
    }
    return null // No error
  }

  openOffcanvasAttendance(attendance: any) {
    this.selectedAttendance = attendance
  }

  onStatusChange() {
    this.currentPage = 1 // Reset to first page when limit changes
    this.loadRequests() // Load employees with new page limit
  }

  getTotalRequestedDays(requests: any[]): number {
    return requests.reduce((sum, req) => {
      if (req.selectionType === 'FullDay') return sum + 1
      if (req.selectionType === 'HalfDay') return sum + 0.5
      return sum
    }, 0)
  }
}
